import React, { useState } from 'react';
import { 
  Play, 
  Pause, 
  Music, 
  Trash2, 
  MoreHorizontal,
  Clock,
  Heart
} from 'lucide-react';
import dataManager from '../utils/dataManager';
import ConfirmDialog from './ConfirmDialog';

const QueuePage = ({ 
  playQueue, 
  currentSongIndex, 
  isPlaying, 
  onPlaySong, 
  onRemoveFromQueue, 
  onClearQueue,
  playMode,
  onPlayModeChange
}) => {
  const [showToast, setShowToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false });

  // 显示提示消息
  const showToastMessage = (message, type = 'success') => {
    setShowToast({ message, type });
    setTimeout(() => setShowToast(null), 2000);
  };

  // 处理清空队列确认
  const handleClearQueueConfirm = () => {
    onClearQueue();
    setConfirmDialog({ open: false });
    showToastMessage('播放队列已清空');
  };

  // 获取平台图标
  const getPlatformInfo = (platform) => {
    const platformInfo = {
      netease: { 
        icon: (
          <img 
            src="/icon/netease.png" 
            alt="网易云音乐" 
            className="w-5 h-5 object-contain"
          />
        )
      },
      kuwo: { 
        icon: (
          <img 
            src="/icon/kw.png" 
            alt="酷我音乐" 
            className="w-5 h-5 object-contain"
          />
        )
      },
      qq: { 
        icon: (
          <img 
            src="/icon/qq.png" 
            alt="QQ音乐" 
            className="w-5 h-5 object-contain"
          />
        )
      }
    };
    return platformInfo[platform] || { 
      icon: (
        <Music className="w-5 h-5 text-gray-600" />
      )
    };
  };

  // 格式化时长
  const formatDuration = (duration) => {
    return duration || '--:--';
  };

  // 格式化添加时间
  const formatAddedTime = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}天前`;
    if (hours > 0) return `${hours}小时前`;
    if (minutes > 0) return `${minutes}分钟前`;
    return '刚刚';
  };

  // 切换收藏状态
  const handleToggleFavorite = (song) => {
    const isLiked = dataManager.isFavorite(song.id, song.platform);
    if (isLiked) {
      dataManager.removeFromFavorites(song.id, song.platform);
      showToastMessage('已从我喜欢的音乐中移除');
    } else {
      dataManager.addToFavorites(song);
      showToastMessage('已添加到我喜欢的音乐');
    }
  };

  // 队列项组件
  const QueueItem = ({ song, index, isCurrentPlaying, onClick }) => {
    const platformInfo = getPlatformInfo(song.platform);
    const isLiked = dataManager.isFavorite(song.id, song.platform);
    
    return (
      <div 
        onClick={() => onClick(index)}
        className={`flex items-center gap-4 p-3 rounded-lg transition-all duration-200 group cursor-pointer ${
          isCurrentPlaying 
            ? 'bg-primary-600/10 border border-primary-300/50' 
            : 'hover:bg-dark-100'
        }`}
      >
        {/* 序号/播放状态 */}
        <div className="w-8 text-center">
          {isCurrentPlaying && isPlaying ? (
            <div className="w-4 h-4 mx-auto">
              <div className="flex items-center justify-center space-x-0.5">
                <div className="w-1 h-3 bg-primary-500 animate-pulse"></div>
                <div className="w-1 h-2 bg-primary-500 animate-pulse animation-delay-75"></div>
                <div className="w-1 h-4 bg-primary-500 animate-pulse animation-delay-150"></div>
              </div>
            </div>
          ) : (
            <span className={`text-sm ${
              isCurrentPlaying ? 'text-primary-600 font-semibold' : 'text-dark-400 group-hover:hidden'
            }`}>
              {index + 1}
            </span>
          )}
          {!isCurrentPlaying && (
            <Play size={14} className="text-primary-500 hidden group-hover:block mx-auto" />
          )}
        </div>

        {/* 平台图标 */}
        <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
          <div className="w-7 h-7 flex items-center justify-center">
            {platformInfo.icon}
          </div>
        </div>

        {/* 歌曲信息 */}
        <div className="flex-1 min-w-0">
          <h4 className={`font-medium truncate transition-colors duration-200 ${
            isCurrentPlaying 
              ? 'text-primary-700 font-semibold' 
              : 'text-dark-800 group-hover:text-primary-600'
          }`}>
            {song.name}
          </h4>
          <p className={`text-sm truncate ${isCurrentPlaying ? 'text-primary-600' : 'text-dark-500'}`}>{song.artist}</p>
        </div>

        {/* 专辑 */}
        <div className="hidden md:block flex-1 min-w-0">
          <p className="text-sm text-dark-500 truncate">{song.album}</p>
        </div>

        {/* 添加时间 */}
        <div className="hidden lg:block text-sm text-dark-400 w-20 text-center">
          {formatAddedTime(song.addedAt)}
        </div>

        {/* 时长 */}
        <div className="text-sm text-dark-400 w-12 text-right">
          {formatDuration(song.duration)}
        </div>

        {/* 操作按钮 */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleToggleFavorite(song);
            }}
            className="p-1 hover:bg-dark-100 rounded transition-colors duration-200"
            title={isLiked ? '取消收藏' : '收藏'}
          >
            <Heart 
              size={14} 
              className={`transition-colors duration-200 ${
                isLiked 
                  ? 'text-red-500 fill-current' 
                  : 'text-dark-400 hover:text-red-500'
              }`} 
            />
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onRemoveFromQueue(index);
            }}
            className="p-1 hover:bg-red-500/20 hover:text-red-400 rounded transition-colors duration-200"
            title="从队列移除"
          >
            <Trash2 size={14} className="text-red-500" />
          </button>
        </div>
      </div>
    );
  };



  return (
    <div className="flex-1 bg-dark-50 overflow-hidden">
      <div className="flex flex-col h-full">
        {/* 固定头部 */}
        <div className="bg-dark-50 border-b border-dark-200/50 flex-shrink-0">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-dark-800 mb-1">播放队列</h1>
                <p className="text-dark-500">
                  共 {playQueue.length} 首歌曲
                  {currentSongIndex >= 0 && (
                    <span className="ml-2">• 正在播放第 {currentSongIndex + 1} 首</span>
                  )}
                </p>
              </div>
              
              {/* 操作按钮 */}
              <div className="flex items-center gap-3">
                {/* 清空队列 */}
                <button 
                  onClick={() => setConfirmDialog({ open: true })}
                  disabled={playQueue.length === 0}
                  className="p-2 text-red-500 hover:bg-red-500 hover:text-white rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="清空播放队列"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 可滚动的队列列表 */}
        <div className="flex-1 overflow-y-auto scrollbar-thin">
          <div className="p-6">
            {playQueue.length > 0 ? (
              <div className="space-y-1">
                {/* 列表头部 */}
                <div className="flex items-center gap-4 px-3 py-2 text-sm text-dark-500 border-b border-dark-200 mb-2">
                  <div className="w-8">#</div>
                  <div className="w-12"></div>
                  <div className="flex-1">标题</div>
                  <div className="hidden md:block flex-1">专辑</div>
                  <div className="hidden lg:block w-20 text-center">添加时间</div>
                  <div className="w-12 text-right">时长</div>
                  <div className="w-16"></div>
                </div>

                {/* 队列项列表 */}
                {playQueue.map((song, index) => (
                  <QueueItem
                    key={`${song.id}_${song.platform}_${index}`}
                    song={song}
                    index={index}
                    isCurrentPlaying={index === currentSongIndex}
                    onClick={onPlaySong}
                  />
                ))}
              </div>
            ) : (
              /* 空状态 */
              <div className="text-center py-12">
                <Music size={48} className="text-dark-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-dark-600 mb-2">播放队列为空</h3>
                <p className="text-dark-500">
                  还没有添加任何歌曲到播放队列，去搜索或音乐库添加一些歌曲吧
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 确认对话框 */}
      <ConfirmDialog
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false })}
        onConfirm={handleClearQueueConfirm}
        title="清空播放队列"
        message="确定要清空播放队列吗？清空后将无法恢复。"
        confirmText="清空"
        cancelText="取消"
        type="danger"
      />

      {/* 提示消息 */}
      {showToast && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50">
          <div className={`px-6 py-3 rounded-2xl shadow-lg backdrop-blur-md ${
            showToast.type === 'success' 
              ? 'bg-green-500/90 text-white' 
              : 'bg-red-500/90 text-white'
          }`}>
            <p className="font-medium">{showToast.message}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueuePage;
